import { inject } from '@angular/core';
import { Router, Routes } from '@angular/router';
import { AuthService } from './core/services/auth-service';
import { SKIP_BACK_ORIGIN } from './core/services/back-origin.service';
import {
  authGuard,
  publicGuard,
  roleRedirectGuard,
  toolPickerGuard,
  requestAccessGuard,
} from './core/guards/auth-guard';
import { accessPendingGuard } from './core/guards/access-pending-guard';
import { dashboardRedirectGuard } from './core/guards/dashboard-redirect-guard';

import { matchHasPermission, matchReviewer, matchAllocator } from './core/guards/role-guard';
import {
  PERMISSIONS,
  canAccessAdminPanel,
  resolveAdminPanelDefaultPath,
} from './core/auth/permissions';

export const adminPanelGuard = async () => {
  const authService = inject(AuthService);
  await authService.waitForAuthState();
  await authService.waitForClaims();

  return canAccessAdminPanel(authService);
};

export const adminDefaultTabGuard = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  await authService.waitForAuthState();
  await authService.waitForClaims();

  return router.createUrlTree([resolveAdminPanelDefaultPath(authService)]);
};

const loadAdminLayout = () =>
  import('./features/admin/admin-layout/admin-layout').then((m) => m.AdminLayoutComponent);

const ADMIN_CHILDREN: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canMatch: [matchHasPermission(PERMISSIONS.USER_MANAGE)],
    loadComponent: () =>
      import('./features/admin/user-management/user-management').then((m) => m.UserManagement),
  },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [adminDefaultTabGuard],
    children: [],
  },
  {
    path: 'access-requests',
    canMatch: [matchHasPermission('user:manage')],
    loadComponent: () =>
      import('./features/admin/access-requests/access-requests').then(
        (m) => m.AccessRequestsComponent,
      ),
  },
  {
    path: 'role-settings',
    canMatch: [matchHasPermission('role:manage')],
    loadComponent: () =>
      import('./features/admin/role-settings/role-settings').then((m) => m.RoleSettingsComponent),
  },
  {
    path: 'construction-vendors',
    canMatch: [matchHasPermission('user:manage')],
    loadComponent: () =>
      import('./features/admin/vendor-management/vendor-management').then(
        (m) => m.VendorManagementComponent,
      ),
  },
  {
    path: 'acceptance-vendors',
    canMatch: [matchHasPermission('user:manage')],
    loadComponent: () =>
      import('./features/admin/acceptance-vendor-management/acceptance-vendor-management').then(
        (m) => m.AcceptanceVendorManagementComponent,
      ),
  },
  {
    path: 'vendor-management',
    redirectTo: 'construction-vendors',
    pathMatch: 'full',
  },
  {
    path: 'market-management',
    canMatch: [matchHasPermission('market:manage')],
    loadComponent: () =>
      import('./features/admin/market-management/market-management').then(
        (m) => m.MarketManagementComponent,
      ),
  },
  {
    path: 'submission-deadlines',
    canMatch: [matchHasPermission('deadline:manage')],
    loadComponent: () =>
      import('./features/admin/submission-deadlines/submission-deadlines').then(
        (m) => m.SubmissionDeadlinesComponent,
      ),
  },
  {
    path: 'deleted-submissions',
    canMatch: [matchHasPermission(PERMISSIONS.REQUEST_RESTORE)],
    loadComponent: () =>
      import('./features/admin/deleted-submissions/deleted-submissions').then(
        (m) => m.DeletedSubmissionsComponent,
      ),
  },
  {
    path: 'deleted-inspections',
    canMatch: [matchHasPermission(PERMISSIONS.INSPECTION_RESTORE)],
    loadComponent: () =>
      import('./features/admin/deleted-inspections/deleted-inspections').then(
        (m) => m.DeletedInspectionsComponent,
      ),
  },
];

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [roleRedirectGuard],
    children: [],
  },
  {
    path: 'login',
    loadComponent: () => import('./features/auth-pages/login/login').then((m) => m.LoginComponent),
    canActivate: [publicGuard],
  },
  {
    path: 'signup',
    loadComponent: () => import('./features/signup/signup').then((m) => m.Signup),
    canActivate: [publicGuard],
  },
  {
    path: 'access-pending',
    loadComponent: () =>
      import('./features/auth-pages/access-pending/access-pending').then(
        (m) => m.AccessPendingComponent,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'request-access',
    loadComponent: () =>
      import('./features/auth-pages/request-access/request-access').then(
        (m) => m.RequestAccessComponent,
      ),
    canActivate: [requestAccessGuard],
  },
  {
    path: 'tool-picker',
    loadComponent: () =>
      import('./features/tool-picker/tool-picker').then((m) => m.ToolPickerComponent),
    canActivate: [toolPickerGuard],
  },
  {
    path: '',
    loadComponent: () => import('./layouts/app-shell/app-shell').then((m) => m.AppShellComponent),
    canActivate: [accessPendingGuard],
    children: [
      // --- Project Acceptance Tool ---
      {
        path: '',
        canMatch: [matchHasPermission(PERMISSIONS.PROJECT_ACCEPTANCE_ACCESS)],
        children: [
          // --- Dashboards ---
          {
            path: 'dashboard',
            pathMatch: 'full',
            canActivate: [dashboardRedirectGuard],
            children: [],
          },
          {
            path: 'dashboard/allocator',
            canMatch: [matchAllocator],
            loadComponent: () =>
              import('./features/dashboards/allocator-dashboard/dashboard').then(
                (m) => m.AllocatorDashboardComponent,
              ),
          },
          {
            path: 'dashboard/a',
            canMatch: [matchHasPermission('dashboard:view')],
            loadComponent: () =>
              import('./features/dashboards/dashboard-a/dashboard-a').then(
                (m) => m.DashboardComponent,
              ),
          },
          {
            path: 'dashboard/vendor',
            redirectTo: '/dashboard/a',
            pathMatch: 'full',
          },
          {
            path: 'dashboard/reviewer',
            canMatch: [matchReviewer],
            data: { view: 'assigned' },
            loadComponent: () =>
              import('./features/dashboards/reviewer-dashboard/reviewer-dashboard').then(
                (m) => m.ReviewerDashboardComponent,
              ),
          },
          {
            path: 'queue',
            canMatch: [matchHasPermission('review:submit')],
            data: { view: 'queue' },
            loadComponent: () =>
              import('./features/dashboards/reviewer-dashboard/reviewer-dashboard').then(
                (m) => m.ReviewerDashboardComponent,
              ),
          },
          // --- Manager Sidenav layout wrapper ---
          {
            path: '',
            canMatch: [matchHasPermission('case:resubmit')],
            loadComponent: () =>
              import('./features/dashboards/manager-layout/manager-layout').then(
                (m) => m.ManagerLayoutComponent,
              ),
            children: [
              {
                path: 'dashboard/manager',
                redirectTo: '/dashboard/a',
                pathMatch: 'full',
              },
              {
                path: 'cases/finished',
                loadComponent: () =>
                  import('./features/cases/finished-cases/finished-cases').then(
                    (m) => m.FinishedCasesComponent,
                  ),
              },
              {
                path: 'cases/all',
                redirectTo: '/cases',
                pathMatch: 'full',
              },
            ],
          },
          // --- Reports ---
          {
            path: 'reports/vendor',
            canMatch: [matchHasPermission('request:create')],
            loadComponent: () =>
              import('./features/reports/vendor-report/vendor-report').then(
                (m) => m.VendorReportComponent,
              ),
          },
          {
            path: 'reports/manager',
            canMatch: [matchHasPermission('case:resubmit')],
            loadComponent: () =>
              import('./features/reports/manager-report/manager-report').then(
                (m) => m.ManagerReportComponent,
              ),
          },
          // --- Requests ---
          {
            path: 'requests',
            canMatch: [matchHasPermission('request:create', 'request:view_all')],
            loadChildren: () =>
              import('./features/requests/requests.routes').then((m) => m.REQUESTS_ROUTES),
          },

          // --- Reviews ---
          {
            path: 'reviews',
            pathMatch: 'full',
            canMatch: [matchHasPermission('review:submit', 'review:override', 'case:view_all')],
            loadComponent: () =>
              import('./features/cases/reviewer-reviews/reviewer-reviews').then(
                (m) => m.ReviewerReviewsComponent,
              ),
          },
          {
            path: 'reviews/:id',
            data: { [SKIP_BACK_ORIGIN]: true },
            canMatch: [
              matchHasPermission(
                'review:submit',
                'review:override',
                'case:finalize',
                'case:amend_closed',
              ),
            ],
            canDeactivate: [
              (comp: { canDeactivate?: () => boolean }) =>
                comp?.canDeactivate ? comp.canDeactivate() : true,
            ],
            loadComponent: () =>
              import('./features/cases/shared-case-view/shared-case-view').then(
                (m) => m.SharedCaseViewComponent,
              ),
          },
          {
            path: 'review/:id',
            redirectTo: 'reviews/:id',
          },

          // --- Cases ---
          {
            path: 'cases',
            canMatch: [
              matchHasPermission(
                'case:view',
                'case:view_all',
                'case_queue:manage',
                'case:finalize',
              ),
            ],
            loadComponent: () =>
              import('./features/dashboards/manager-layout/manager-layout').then(
                (m) => m.ManagerLayoutComponent,
              ),
            loadChildren: () => import('./features/cases/cases.routes').then((m) => m.CASES_ROUTES),
          },
          {
            path: 'cases/:id',
            data: { [SKIP_BACK_ORIGIN]: true },
            canMatch: [matchHasPermission('case:view', 'case:view_all')],
            loadComponent: () =>
              import('./features/cases/case-detail/case-detail').then((m) => m.CaseDetailComponent),
          },
        ],
      },
      // --- On-site Inspection Tool ---
      {
        path: 'inspections',
        canMatch: [matchHasPermission(PERMISSIONS.INSPECTION_ACCESS)],
        loadChildren: () =>
          import('./features/inspections/inspections.routes').then((m) => m.INSPECTIONS_ROUTES),
      },
      // --- Admin ---
      {
        path: 'admin-panel',
        canMatch: [adminPanelGuard],
        loadComponent: loadAdminLayout,
        children: ADMIN_CHILDREN,
      },
      {
        path: 'admin',
        canMatch: [adminPanelGuard],
        loadComponent: loadAdminLayout,
        children: ADMIN_CHILDREN,
      },

      // --- General ---
      {
        path: 'no-role',
        loadComponent: () =>
          import('./features/auth-pages/no-role/no-role').then((m) => m.NoRoleComponent),
        canActivate: [authGuard],
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
